import { WeeklyCopySettings } from '../home/WeeklyCopySettings';
import { PersonalMessageSettings } from '../messages/PersonalMessageSettings';
import { useEffect, useId, useRef, useState } from 'react';
import { LogOut, UserRound, MessageSquare, Copy } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useI18n } from '../../contexts/I18nContext';

export function AccountMenu() {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const [isOpen, setIsOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [copySettingsOpen, setCopySettingsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const id = useId();

  useEffect(() => {
    if (!isOpen) return;
    menuRef.current?.querySelector<HTMLButtonElement>('[role="menuitem"]')?.focus();
    const dismissOutside = (event: PointerEvent) => {
      if (event.target instanceof Node && !containerRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('pointerdown', dismissOutside);
    return () => document.removeEventListener('pointerdown', dismissOutside);
  }, [isOpen]);

  return (
    <>
      <div
        ref={containerRef}
        className="relative shrink-0"
        onBlur={event => {
          if (!event.currentTarget.contains(event.relatedTarget)) {
            setIsOpen(false);
          }
        }}
        onKeyDown={event => {
          if (event.key === 'Escape' && isOpen) {
            event.preventDefault();
            event.stopPropagation();
            setIsOpen(false);
            triggerRef.current?.focus();
          }
        }}
      >
        <button
          ref={triggerRef}
          id={`${id}-trigger`}
          type="button"
          aria-label={t('nav.accountMenu')}
          title={t('nav.accountMenu')}
          aria-haspopup="menu"
          aria-expanded={isOpen}
          aria-controls={isOpen ? `${id}-menu` : undefined}
          onClick={() => setIsOpen(open => !open)}
          onKeyDown={event => {
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
              event.preventDefault();
              setIsOpen(true);
            }
          }}
          className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-gray-100 text-gray-700 hover:bg-gray-200 aria-expanded:bg-gray-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
        >
          <UserRound className="h-5 w-5" aria-hidden="true" />
        </button>
        {isOpen && (
          <div className="absolute right-0 z-50 mt-2 w-64 max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl bg-white shadow-lg">
            <div className="border-b border-gray-100 px-4 py-3">
              <p className="break-words text-sm font-semibold text-gray-900">
                {user?.name?.trim() || t('nav.account')}
              </p>
            </div>
            <div
              ref={menuRef}
              id={`${id}-menu`}
              role="menu"
              aria-labelledby={`${id}-trigger`}
              className="p-1"
              onKeyDown={event => {
                if (event.key === 'Tab' && event.shiftKey) {
                  event.preventDefault();
                  setIsOpen(false);
                  triggerRef.current?.focus();
                  return;
                }
                const items = Array.from(
                  event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="menuitem"]')
                );
                const index = items.indexOf(document.activeElement as HTMLButtonElement);
                let next: number;
                switch (event.key) {
                  case 'ArrowDown':
                    next = (index + 1) % items.length;
                    break;
                  case 'ArrowUp':
                    next = (index - 1 + items.length) % items.length;
                    break;
                  case 'Home':
                    next = 0;
                    break;
                  case 'End':
                    next = items.length - 1;
                    break;
                  default:
                    return;
                }
                event.preventDefault();
                items[next]?.focus();
              }}
            >
              <button
                type="button"
                role="menuitem"
                tabIndex={-1}
                onClick={() => {
                  setIsOpen(false);
                  triggerRef.current?.focus();
                  setSettingsOpen(true);
                }}
                className="flex min-h-11 w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm font-medium text-gray-700 hover:bg-gray-50 focus-visible:bg-primary-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500"
              >
                <MessageSquare className="h-4 w-4 shrink-0" aria-hidden="true" />
                {t('message.settings')}
              </button>
              <button
                type="button"
                role="menuitem"
                tabIndex={-1}
                onClick={() => {
                  setIsOpen(false);
                  triggerRef.current?.focus();
                  setCopySettingsOpen(true);
                }}
                className="flex min-h-11 w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm font-medium text-gray-700 hover:bg-gray-50 focus-visible:bg-primary-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500"
              >
                <Copy className="h-4 w-4 shrink-0" aria-hidden="true" />
                {t('weeklyCopy.settings')}
              </button>
              <button
                type="button"
                role="menuitem"
                tabIndex={-1}
                onClick={() => {
                  setIsOpen(false);
                  logout();
                }}
                className="flex min-h-11 w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm font-medium text-gray-700 hover:bg-gray-50 focus-visible:bg-primary-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500"
              >
                <LogOut className="h-4 w-4 shrink-0" aria-hidden="true" />
                {t('nav.logout')}
              </button>
            </div>
          </div>
        )}
      </div>
      {copySettingsOpen && (
        <WeeklyCopySettings key={user?.id} onClose={() => setCopySettingsOpen(false)} />
      )}
      {settingsOpen && (
        <PersonalMessageSettings key={user?.id} onClose={() => setSettingsOpen(false)} />
      )}
    </>
  );
}
